import { useMutation, useQuery } from '@apollo/client'
import { Edit } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { gql } from '__generated__'
import { format } from 'date-fns'
import { type FormikConfig, useFormik } from 'formik'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getFullName, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'

const GetCandidatePublicBio = gql(/* GraphQL */ `
    query GetCandidatePublicBio($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            bio {
                id
                bio
                updatedByUser {
                    id
                    firstName
                    nickname
                    lastName
                }
                updatedAt
            }
        }
    }
`)

const UpdateCandidatePublicBio = gql(/* GraphQL */ `
    mutation UpdateCandidatePublicBio($candidateId: ID!, $bio: String!) {
        updateCandidatePublicBio(candidateId: $candidateId, bio: $bio) {
            success
            message
        }
    }
`)

interface FormValues {
    bio?: string
}

const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object({
    bio: Yup.string(),
})

export function CandidatePublicBioPanel() {
    const toast = useToast()
    const { candidateId } = useParams()
    const [isBeingEdited, setIsBeingEdited] = useState(false)
    const [auditString, setAuditString] = useState('')
    const [formValues, setFormValues] = useState<FormValues>({
        bio: '',
    })

    const { loading: isLoading } = useQuery(GetCandidatePublicBio, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted: (data) => {
            setFormValues({
                bio: data.candidate?.bio?.bio || '',
            })

            const user = data.candidate?.bio?.updatedByUser
            const auditDate = new Date(data.candidate?.bio?.updatedAt)

            // If the `updatedAt` field is null, the audit date's value will be
            // "now" (epoch time 0). If it is undefined or another invalid date
            // string, the audit date's value will be NaN. Treat both of these
            // falsey values as indicating the value has never been set.
            if (!auditDate.valueOf()) return

            setAuditString(
                `Last Edited: ${format(auditDate, 'MM/dd/yyyy hh:mm a')} ${user ? getFullName(user) : 'Unknown User'}`
            )
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate bio data'
                ),
            })
        },
    })

    const [updateCandidatePublicBio] = useMutation(UpdateCandidatePublicBio, {
        refetchQueries: [GetCandidatePublicBio],
        awaitRefetchQueries: true,
        onCompleted: (data) => {
            if (data.updateCandidatePublicBio.success) {
                toast({
                    title: 'Success!',
                    message: 'Candidate bio has been updated successfully',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message: `Failed to update candidate bio: ${data.updateCandidatePublicBio.message}`,
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to update candidate bio'
                ),
            })
        },
    })

    const handleEnableEditing = () => {
        setIsBeingEdited(true)
    }

    const handleUpdatePublicBio: FormikConfig<FormValues>['onSubmit'] = (
        values
    ) => {
        updateCandidatePublicBio({
            variables: {
                candidateId: candidateId || '',
                bio: values.bio || '',
            },
        })
        setFormValues(values)
        setIsBeingEdited(false)
    }

    const handleCancel = () => {
        formik.resetForm()
        setIsBeingEdited(false)
    }

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: formValues,
        validationSchema,
        onSubmit: handleUpdatePublicBio,
    })

    return (
        <ProfilePanel
            panelTitle="Public Bio"
            panelSubtitle={auditString}
            actionElements={[
                <IconButton
                    key="edit"
                    onClick={handleEnableEditing}
                    disabled={isBeingEdited}
                >
                    <Edit />
                </IconButton>,
            ]}
            maxHeight="256px"
        >
            {isLoading ? (
                <Box flex="1">
                    <LoadingPanel />
                </Box>
            ) : isBeingEdited ? (
                <>
                    <TextField
                        autoFocus
                        error={formik.touched.bio && Boolean(formik.errors.bio)}
                        fullWidth
                        helperText={formik.touched.bio && formik.errors.bio}
                        label="Bio"
                        multiline
                        name="bio"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        rows="3"
                        value={formik.values.bio}
                    />

                    <Stack direction="row" gap={getPxAsRem(8)}>
                        <Button variant="outlined" onClick={handleCancel}>
                            Cancel
                        </Button>

                        <Button
                            disabled={!formik.dirty}
                            onClick={formik.submitForm}
                        >
                            Save & Update
                        </Button>
                    </Stack>
                </>
            ) : (
                <Box overflow="auto">
                    <Typography sx={{ whiteSpace: 'pre-line' }}>
                        {formik.values.bio || '-'}
                    </Typography>
                </Box>
            )}
        </ProfilePanel>
    )
}
